import {
  isToday,
  isYesterday,
  isThisWeek,
  isThisMonth,
  format,
} from "date-fns";

//creating a timestamp for TimeAgo.
export const convertTimestamp = (timestamp: any) => {
  if (!timestamp || isNaN(new Date(timestamp).getTime())) {
    return ""; // Return an empty string for undefined or invalid timestamps
  }

  const now = new Date();
  const pastDate = new Date(timestamp);
  const timeDifference = now.getTime() - pastDate.getTime();

  const seconds = Math.floor(timeDifference / 1000);

  if (seconds < 60) {
    return `${seconds} s`;
  }

  const minutes = Math.floor(seconds / 60);

  if (minutes < 60) {
    return `${minutes} m`;
  }

  const hours = Math.floor(minutes / 60);

  if (hours < 24) {
    return `${hours} h`;
  }

  const days = Math.floor(hours / 24);

  if (days < 7) {
    return `${days} d`;
  }

  const weeks = Math.floor(days / 7);

  if (weeks <= 2) {
    return `${weeks} w`;
  }

  const years = Math.floor(weeks / 52);

  if (years > 0) {
    const options: Intl.DateTimeFormatOptions = {
      day: "numeric",
      month: "short",
      year: "numeric",
    };
    return pastDate.getFullYear() === now.getFullYear()
      ? pastDate.toLocaleDateString(undefined, {
          day: "numeric",
          month: "short",
        })
      : pastDate.toLocaleDateString(undefined, options);
  }

  const months: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "numeric",
  };
  if (months) return pastDate.toLocaleDateString(undefined, months);
};

export const getFormattedDate = (dateString: string) => {
  const date = new Date(dateString);
  let formattedDate;

  if (isToday(date)) {
    formattedDate = "Today";
  } else if (isYesterday(date)) {
    formattedDate = "Yesterday";
  } else if (isThisWeek(date)) {
    formattedDate = format(date, "iiii"); // Display the day name (Monday, Tuesday, etc.)
  } else if (isThisMonth(date)) {
    formattedDate = format(date, "d MMMM"); // Display date with month (e.g., 5 February)
  } else {
    formattedDate = format(date, "d MMMM yyyy"); // Display date with month and year (e.g., 5 February 2023)
  }

  return formattedDate;
};

interface Timestamp {
  seconds?: number;
  nanoseconds?: number;
}

export const getFormattedDates = (timestamp: Timestamp | null) => {
  const date = timestamp
    ? new Date(
        (timestamp.seconds ?? 0) * 1000 + (timestamp.nanoseconds ?? 0) / 1e6
      )
    : new Date();

  let formattedDate;

  if (isToday(date)) {
    formattedDate = "Today";
  } else if (isYesterday(date)) {
    formattedDate = "Yesterday";
  } else if (isThisWeek(date)) {
    formattedDate = format(date, "iiii"); // Display the day name (Monday, Tuesday, etc.)
  } else if (isThisMonth(date)) {
    formattedDate = format(date, "d MMMM"); // Display date with month (e.g., 5 February)
  } else {
    formattedDate = format(date, "d MMMM yyyy"); // Display date with month and year (e.g., 5 February 2023)
  }

  return formattedDate;
};

export const extractTimeFromDateTime = (dateTimeString: string | undefined) => {
  if (!dateTimeString) {
    return "";
  }

  const dateObj = new Date(dateTimeString);

  // Check if the date object is valid
  if (isNaN(dateObj.getTime())) {
    console.error("Invalid date string:", dateTimeString);
    return "";
  }

  let hours = dateObj.getHours();
  const minutes = dateObj.getMinutes();
  const ampm = hours >= 12 ? "pm" : "am";

  // Convert 24-hour time to 12-hour time
  if (hours > 12) {
    hours -= 12;
  }

  // Handle midnight (00:00) as 12:00 am
  if (hours === 0) {
    hours = 12;
  }

  const formattedTime = `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")} ${ampm}`;

  return formattedTime;
};

// const lastSignInTime = "Mon, 08 Apr 2024 09:05:20 GMT";
// const res = extractTimeFromDateTime(lastSignInTime);
// console.log(res);
